import { useEffect, useMemo, useState } from "react";
import ComplianceNotice from "@/components/ComplianceNotice";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ExternalLink, RotateCcw, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

function fillPrompt(template, campaign) {
  if (!template) return "";
  const office = (campaign?.office_sought || "").trim() || "this office";
  const district = (campaign?.district || "").trim() || "my area";
  const name = (campaign?.candidate_name || "").trim() || "the candidate";
  return template
    .replace(/\{\{\s*office_sought\s*\}\}/g, office)
    .replace(/\{\{\s*district\s*\}\}/g, district)
    .replace(/\{\{\s*candidate_name\s*\}\}/g, name);
}

export default function LaunchChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null); // { phases, checked_item_ids, total_items, completed_count }
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [pending, setPending] = useState({}); // optimistic toggle in-flight

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [checklistRes, campaignRes] = await Promise.all([
        api.get("/launch-checklist"),
        api.get("/campaign").catch(() => ({ data: null })),
      ]);
      setData(checklistRes.data);
      setCampaign(campaignRes.data || null);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const checkedSet = useMemo(() => new Set(data?.checked_item_ids || []), [data]);

  const toggle = async (itemId, nextChecked) => {
    if (!data) return;
    setPending((p) => ({ ...p, [itemId]: true }));
    // Optimistic update
    const optimistic = new Set(checkedSet);
    if (nextChecked) optimistic.add(itemId);
    else optimistic.delete(itemId);
    setData((d) => ({
      ...d,
      checked_item_ids: Array.from(optimistic),
      completed_count: optimistic.size,
    }));
    try {
      const { data: resp } = await api.post("/launch-checklist/toggle", {
        item_id: itemId,
        checked: nextChecked,
      });
      setData((d) => ({ ...d, ...resp }));
    } catch (err) {
      // Rollback
      const rolled = new Set(checkedSet);
      setData((d) => ({
        ...d,
        checked_item_ids: Array.from(rolled),
        completed_count: rolled.size,
      }));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[itemId];
        return next;
      });
    }
  };

  const reset = async () => {
    try {
      const { data: resp } = await api.post("/launch-checklist/reset");
      setData((d) => ({ ...d, ...resp }));
      toast.success("Checklist reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12 text-slate-500" data-testid="launch-loading">Loading…</div>;

  if (error) {
    const isProfile = /profile|intake/i.test(error);
    const isCandidate = /candidate accounts only/i.test(error);
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-sm">
          <div className="h-10 w-10 rounded-lg bg-flag-red/10 text-flag-red flex items-center justify-center mx-auto">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h1 className="font-display text-2xl font-bold text-flag-navy mt-4">
            {isCandidate ? "Candidate accounts only" : isProfile ? "Finish your intake first" : "Couldn't load the checklist"}
          </h1>
          <p className="text-slate-600 mt-2">{error}</p>
          {(isProfile || isCandidate) && (
            <Link to="/intake" data-testid="launch-no-profile-cta">
              <Button className="mt-6 bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">
                Go to intake
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  const { phases, total_items, completed_count } = data;
  const pct = total_items > 0 ? Math.round((completed_count / total_items) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Launch Checklist</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Run a real campaign, in order.</h1>
          <p className="text-slate-600 mt-2">A sequenced master list of everything a first-time candidate needs to do. Work top to bottom. Check things off as you go.</p>
          <ComplianceNotice kind="deadline" className="mt-4 max-w-2xl" />
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              data-testid="launch-reset-btn"
              className="h-10 px-3 text-slate-600 hover:text-flag-red font-semibold"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset the checklist?</AlertDialogTitle>
              <AlertDialogDescription>Every item will be unchecked. Items themselves are not deleted — you'll just start the progress over.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="launch-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={reset} data-testid="launch-reset-confirm" className="bg-flag-red hover:bg-flag-red-hover">
                Reset
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Progress */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6" data-testid="launch-progress-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className={`h-5 w-5 ${completed_count > 0 ? "text-green-700" : "text-slate-300"}`} />
            <span className="font-display font-bold text-flag-navy text-lg" data-testid="launch-progress-text">
              {completed_count} of {total_items} complete
            </span>
          </div>
          <span className="text-sm font-bold text-slate-600">{pct}%</span>
        </div>
        <div className="mt-3 h-3 w-full bg-slate-100 rounded-full overflow-hidden" data-testid="launch-progress-bar-track">
          <div
            className="h-full bg-flag-red rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
            data-testid="launch-progress-bar"
          />
        </div>
      </div>

      {/* Phases */}
      <div className="mt-6 space-y-4">
        {phases.map((phase) => {
          const phaseChecked = phase.items.filter((i) => checkedSet.has(i.id)).length;
          const phaseTotal = phase.items.length;
          const phaseDone = phaseChecked === phaseTotal;
          const isCollapsed = !!collapsed[phase.id];
          return (
            <section
              key={phase.id}
              className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
              data-testid={`launch-phase-${phase.id}`}
            >
              <button
                type="button"
                onClick={() => setCollapsed((c) => ({ ...c, [phase.id]: !isCollapsed }))}
                data-testid={`launch-phase-toggle-${phase.id}`}
                className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isCollapsed ? <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
                  <div className="text-left min-w-0">
                    <div className="font-display font-bold text-flag-navy truncate">{phase.title}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5">
                      {phaseChecked} of {phaseTotal}
                      {phaseDone && <span className="ml-1.5 inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3 w-3" /> done</span>}
                    </div>
                  </div>
                </div>
                <div className="h-1.5 w-24 sm:w-32 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${phaseDone ? "bg-green-700" : "bg-flag-red"}`}
                    style={{ width: `${phaseTotal > 0 ? Math.round((phaseChecked / phaseTotal) * 100) : 0}%` }}
                  />
                </div>
              </button>

              {!isCollapsed && (
                <ul className="border-t border-slate-100 divide-y divide-slate-100" data-testid={`launch-items-${phase.id}`}>
                  {phase.items.map((item) => {
                    const isChecked = checkedSet.has(item.id);
                    const isPending = !!pending[item.id];
                    return (
                      <li
                        key={item.id}
                        className={`px-5 sm:px-6 py-3.5 flex items-start gap-3 ${isPending ? "opacity-70" : ""}`}
                        data-testid={`launch-item-${item.id}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(v) => toggle(item.id, !!v)}
                          disabled={isPending}
                          data-testid={`launch-checkbox-${item.id}`}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <label
                            htmlFor={`cb-${item.id}`}
                            className={`block text-base sm:text-lg leading-snug font-semibold cursor-pointer ${
                              isChecked ? "text-slate-400 line-through" : "text-flag-navy"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isPending) toggle(item.id, !isChecked);
                            }}
                          >
                            {item.text}
                          </label>
                          {item.tool_link && (
                            <Link
                              to={item.tool_link.to}
                              data-testid={`launch-item-link-${item.id}`}
                              className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-flag-red hover:underline"
                            >
                              Open the {item.tool_link.label} <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                          {item.help_prompt && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const filled = fillPrompt(item.help_prompt, campaign);
                                navigate(
                                  `/advisor?new=1&from=${encodeURIComponent(item.id)}&prompt=${encodeURIComponent(filled)}`,
                                );
                              }}
                              data-testid={`launch-item-help-${item.id}`}
                              className="mt-2 ml-0 sm:ml-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-flag-red text-white hover:bg-flag-red-hover rounded-full px-2.5 py-1 shadow-sm transition-colors"
                            >
                              <HelpCircle className="h-3 w-3" /> Don&rsquo;t know how? Ask Advisor! Click here.
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
